import React, { useState } from 'react';
import { X, CheckCircle, Brain, Target, TrendingUp,AlertCircle } from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../../services/api';

const QUESTIONS = [
  {
    id: 1,
    question: "How comfortable are you with basic programming concepts?",
    options: [
      { text: "Not familiar at all", score: 1, category: "technical" },
      { text: "Know the basics", score: 2, category: "technical" },
      { text: "Comfortable with fundamentals", score: 3, category: "technical" },
      { text: "Very experienced", score: 4, category: "technical" },
      { text: "Expert level", score: 5, category: "technical" }
    ]
  },
  {
    id: 2,
    question: "How would you rate your problem-solving skills?",
    options: [
      { text: "Need significant help", score: 1, category: "problem_solving" },
      { text: "Can solve simple problems", score: 2, category: "problem_solving" },
      { text: "Handle moderate complexity", score: 3, category: "problem_solving" },
      { text: "Excellent at complex problems", score: 4, category: "problem_solving" },
      { text: "Exceptional problem solver", score: 5, category: "problem_solving" }
    ]
  },
  {
    id: 3,
    question: "Rate your experience with logical reasoning and mathematics:",
    options: [
      { text: "Struggle with basic math", score: 1, category: "numeric_reasoning" },
      { text: "Basic arithmetic is fine", score: 2, category: "numeric_reasoning" },
      { text: "Comfortable with algebra", score: 3, category: "numeric_reasoning" },
      { text: "Advanced math concepts", score: 4, category: "numeric_reasoning" },
      { text: "Mathematics expert", score: 5, category: "numeric_reasoning" }
    ]
  },
  {
    id: 4,
    question: "How proficient are you with written communication?",
    options: [
      { text: "Poor writing skills", score: 1, category: "verbal_reasoning" },
      { text: "Basic written communication", score: 2, category: "verbal_reasoning" },
      { text: "Good at expressing ideas", score: 3, category: "verbal_reasoning" },
      { text: "Strong writing and communication", score: 4, category: "verbal_reasoning" },
      { text: "Exceptional communicator", score: 5, category: "verbal_reasoning" }
    ]
  },
  {
    id: 5,
    question: "Rate your familiarity with computers and digital tools:",
    options: [
      { text: "Basic computer user", score: 1, category: "digital_skills" },
      { text: "Comfortable with common software", score: 2, category: "digital_skills" },
      { text: "Advanced software user", score: 3, category: "digital_skills" },
      { text: "Tech-savvy and adaptable", score: 4, category: "digital_skills" },
      { text: "Technology expert", score: 5, category: "digital_skills" }
    ]
  },
  {
    id: 6,
    question: "How do you handle challenging or complex learning situations?",
    options: [
      { text: "Get frustrated easily", score: 1, category: "difficulty_tolerance" },
      { text: "Need some support", score: 2, category: "difficulty_tolerance" },
      { text: "Can manage with effort", score: 3, category: "difficulty_tolerance" },
      { text: "Thrive on challenges", score: 4, category: "difficulty_tolerance" },
      { text: "Love complex problems", score: 5, category: "difficulty_tolerance" }
    ]
  },
  {
    id: 7,
    question: "How quickly do you typically learn new concepts?",
    options: [
      { text: "Very slowly", score: 1, category: "learning_speed" },
      { text: "Need more time than average", score: 2, category: "learning_speed" },
      { text: "Average learning pace", score: 3, category: "learning_speed" },
      { text: "Learn faster than average", score: 4, category: "learning_speed" },
      { text: "Very quick learner", score: 5, category: "learning_speed" }
    ]
  },
  {
    id: 8,
    question: "How strong is your ability to remember and retain information?",
    options: [
      { text: "Poor memory retention", score: 1, category: "memory_strength" },
      { text: "Average memory", score: 2, category: "memory_strength" },
      { text: "Good at remembering", score: 3, category: "memory_strength" },
      { text: "Strong memory skills", score: 4, category: "memory_strength" },
      { text: "Exceptional memory", score: 5, category: "memory_strength" }
    ]
  }
];

const AssessmentModal = ({ isOpen, onClose, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  const calculateResults = () => {
    const scores = {
      numeric_reasoning_score: 0,
      verbal_reasoning_score: 0,
      technical_aptitude_score: 0,
      digital_skills_score: 0,
      problem_solving_score: 0,
      domain_specific_score: 0,
      learning_speed: 0,
      difficulty_tolerance: 0,
      memory_strength: 0,
      concept_understanding: 0
    };

    let totalScore = 0;
    let answerCount = 0;

    Object.entries(answers).forEach(([questionId, answer]) => {
      const question = QUESTIONS.find(q => q.id === parseInt(questionId));
      if (question && answer) {
        const score = answer.score;
        totalScore += score;
        answerCount++;

        // Map to assessment categories
        switch (question.category) {
          case 'numeric_reasoning':
            scores.numeric_reasoning_score += score;
            break;
          case 'verbal_reasoning':
            scores.verbal_reasoning_score += score;
            break;
          case 'technical':
            scores.technical_aptitude_score += score;
            break;
          case 'digital_skills':
            scores.digital_skills_score += score;
            break;
          case 'problem_solving':
            scores.problem_solving_score += score;
            break;
          case 'difficulty_tolerance':
            scores.difficulty_tolerance += score;
            break;
          case 'learning_speed':
            scores.learning_speed += score;
            break;
          case 'memory_strength':
            scores.memory_strength += score;
            break;
          default:
            break;
        }
      }
    });

    // Normalize scores (assuming 8 questions per category, max 5 per question)
    Object.keys(scores).forEach(key => {
      if (key.includes('score')) {
        scores[key] = Math.round((scores[key] / 5) * 20); // Convert to 0-100 scale
      }
    });

    // Calculate AI attributes
    const avgScore = totalScore / answerCount;
    scores.consistency_index = Math.random() * 0.3 + 0.7; // Mock consistency
    scores.concept_understanding = Math.round(avgScore * 20);

    // Determine level and difficulty
    if (avgScore >= 4) {
      scores.current_level = "Advanced";
      scores.recommended_difficulty = "Expert";
    } else if (avgScore >= 3) {
      scores.current_level = "Intermediate";
      scores.recommended_difficulty = "Medium-Hard";
    } else {
      scores.current_level = "Beginner";
      scores.recommended_difficulty = "Easy-Medium";
    }

    scores.test_version = "v1.0";
    return scores;
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < QUESTIONS.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setIsSubmitting(true);
    try {
      const results = calculateResults();
      
      console.log('Submitting assessment results:', results);
      
      // ✅ DIRECT API CALL - no dynamic import
      const response = await api.post('/users/assessment', {
        summary: results
      });

      console.log('Assessment submission response:', response);

      setResults(results);
      setShowResults(true);
      toast.success('Assessment completed successfully!');
      
      if (onComplete) {
        onComplete(results);
      }
    } catch (error) {
      console.error('Assessment submission error:', error);
      console.error('Error details:', error.response?.data);
      
      // More specific error messages
      if (error.response?.status === 401) {
        toast.error('Please login to submit assessment');
      } else if (error.response?.status === 400) {
        toast.error('Assessment data is invalid');
      } else {
        toast.error('Failed to submit assessment. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const resetAssessment = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResults(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Brain className="h-6 w-6 text-primary-600" />
                  <h2 className="text-xl font-semibold">Skill Assessment</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {!showResults ? (
                // Assessment Questions
                <div className="space-y-6">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
                    <span>{Math.round(((currentQuestion + 1) / QUESTIONS.length) * 100)}% Complete</span>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((currentQuestion + 1) / QUESTIONS.length) * 100}%` }}
                    ></div>
                  </div>

                  <QuestionDisplay
                    question={QUESTIONS[currentQuestion]}
                    selectedAnswer={answers[QUESTIONS[currentQuestion].id]}
                    onAnswer={(answer) => {
                      setAnswers(prev => ({
                        ...prev,
                        [QUESTIONS[currentQuestion].id]: answer
                      }));
                    }}
                  />

                  <div className="flex justify-between">
                    <button
                      onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                      disabled={currentQuestion === 0}
                      className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
                    >
                      Previous
                    </button>
                    
                    {currentQuestion === QUESTIONS.length - 1 ? (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <LoadingSpinner size="sm" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Assessment'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setCurrentQuestion(prev => prev + 1)}
                        disabled={!answers[QUESTIONS[currentQuestion].id]}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <ResultsDisplay results={results} onClose={onClose} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const QuestionDisplay = ({ question, selectedAnswer, onAnswer }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-medium mb-4">{question.question}</h3>
      <div className="space-y-3">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => onAnswer(option)}
            className={`w-full text-left p-4 border rounded-lg hover:border-primary-300 transition-colors ${
              selectedAnswer?.text === option.text
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <span>{option.text}</span>
              {selectedAnswer?.text === option.text && (
                <CheckCircle className="h-5 w-5 text-primary-600" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

const ResultsDisplay = ({ results, onRetake }) => (
  <div className="space-y-6">
    {/* Overview */}
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
        <Target className="h-8 w-8 text-primary-600" />
      </div>
      <h3 className="text-xl font-semibold mb-2">Assessment Complete!</h3>
      <p className="text-gray-600">
        Your current level: <span className="font-semibold text-primary-600">{results?.current_level}</span>
      </p>
    </div>

    {/* Scores */}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Object.entries(results || {}).filter(([key, value]) => 
        key.includes('score') && typeof value === 'number'
      ).map(([key, value]) => (
        <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-primary-600">{value}</div>
          <div className="text-xs text-gray-600 capitalize">
            {key.replace('_score', '').replace('_', ' ')}
          </div>
        </div>
      ))}
    </div>

    {/* AI Insights */}
    <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-lg">
      <h4 className="font-semibold mb-4 flex items-center gap-2">
        <TrendingUp className="h-5 w-5" />
        AI-Powered Insights
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Learning Speed:</span>
          <span className="font-medium ml-2">{results?.learning_speed || 'Average'}</span>
        </div>
        <div>
          <span className="text-gray-600">Difficulty Tolerance:</span>
          <span className="font-medium ml-2">{results?.difficulty_tolerance || 'Medium'}</span>
        </div>
        <div>
          <span className="text-gray-600">Memory Strength:</span>
          <span className="font-medium ml-2">{results?.memory_strength || 'Average'}</span>
        </div>
        <div>
          <span className="text-gray-600">Recommended Level:</span>
          <span className="font-medium ml-2">{results?.recommended_difficulty}</span>
        </div>
      </div>
    </div>

    {/* Actions */}
    <div className="flex gap-3 justify-center">
      <button
        onClick={onRetake}
        className="px-6 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
      >
        Retake Assessment
      </button>
      <button
        onClick={() => window.location.href = '/learner'}
        className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
      >
        View Dashboard
      </button>
    </div>
  </div>
);

export default AssessmentModal;